import { useEffect } from 'react'
import styled, { keyframes } from 'styled-components'
import { useNavigate } from 'react-router-dom'

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`

const ToastContainer = styled.div<{ closing?: boolean }>`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 400px;
  background: white;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  animation: ${({ closing }) => (closing ? slideOut : slideIn)} 0.2s ease-out forwards;
  overflow: hidden;
`

const ToastHeader = styled.div<{ type: string }>`
  background: ${({ type }) => type === 'proyecto' ? '#4CAF50' : '#2196F3'};
  color: white;
  padding: 0.75rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const HeaderContent = styled.div`
  flex: 1;
  min-width: 0;
`

const ToastTitle = styled.strong`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const ToastSubtitle = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  opacity: 0.9;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  margin-left: 8px;

  &:hover {
    opacity: 0.8;
  }
`

const ToastSummary = styled.div`
  padding: 0.75rem 1rem;
  background: ${({ theme }) => theme.colors.secondary};
  display: flex;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const TotalCapacity = styled.span<{ color: string }>`
  font-weight: 700;
  color: ${({ color }) => color};
`

const ToastMembers = styled.div`
  padding: 0.75rem 1rem;
  max-height: 350px;
  overflow-y: auto;
`

const MemberItem = styled.div`
  padding: 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: 0.5rem;
  background: #f5f5f5;
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &:last-child {
    margin-bottom: 0;
  }
`

const MemberHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
`

const MemberName = styled.span`
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  flex: 1;
  margin-right: 8px;
`

const MemberLoad = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const LoadLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`

const LoadValue = styled.span<{ color: string }>`
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ color }) => color};
`

const ProgressBar = styled.div`
  height: 6px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
  overflow: hidden;
`

const ProgressFill = styled.div<{ width: number; color: string }>`
  height: 100%;
  width: ${({ width }) => Math.min(width, 100)}%;
  background: ${({ color }) => color};
  border-radius: 3px;
  transition: width 0.3s ease;
`

const EmptyMessage = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  padding: 1rem;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const ViewDetailsButton = styled.button`
  width: 100%;
  padding: 0.75rem;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #d0dce0;
  }
`

interface TeamMember {
    userId: number | string
    displayName: string
    load: number
}

interface OpportunityTeamToastProps {
    opportunity: {
        id: number
        name: string
        type: 'oferta' | 'proyecto'
        team: TeamMember[]
    }
    onClose: () => void
    isAdmin?: boolean
    onViewDetails?: (opportunityId: number) => void
}

const getLoadColor = (capacity: number): string => {
    if (capacity === 0) return '#9E9E9E'
    if (capacity < 50) return '#4CAF50'
    if (capacity <= 80) return '#FF9800'
    return '#F44336'
}

export const OpportunityTeamToast: React.FC<OpportunityTeamToastProps> = ({
    opportunity,
    onClose,
    isAdmin = false,
    onViewDetails,
}) => {
    const navigate = useNavigate()
    const team = opportunity.team || []
    const totalLoad = team.reduce((sum, m) => sum + (m.load || 0), 0)

    useEffect(() => {
        const timer = setTimeout(() => {
            onClose()
        }, 15000) // 15 seconds

        return () => clearTimeout(timer)
    }, [onClose])

    const handleViewDetails = () => {
        if (onViewDetails) {
            onViewDetails(opportunity.id)
        } else {
            navigate(`/oportunidades`, { state: { openOpportunityId: opportunity.id } })
        }
    }

    return (
        <ToastContainer>
            <ToastHeader type={opportunity.type}>
                <HeaderContent>
                    <ToastTitle title={opportunity.name}>
                        {opportunity.type === 'proyecto' ? '🚀' : '📋'} {opportunity.name}
                    </ToastTitle>
                    <ToastSubtitle>
                        {opportunity.type === 'proyecto' ? 'Proyecto' : 'Oferta'}
                    </ToastSubtitle>
                </HeaderContent>
                <CloseButton onClick={onClose}>&times;</CloseButton>
            </ToastHeader>

            <ToastSummary>
                <span>👥 Equipo asignado: {team.length} persona{team.length !== 1 ? 's' : ''}</span>
                {isAdmin && (
                    <TotalCapacity color={getLoadColor(totalLoad / Math.max(team.length, 1))}>
                        Total: {totalLoad}%
                    </TotalCapacity>
                )}
            </ToastSummary>

            <ToastMembers>
                {team.length === 0 ? (
                    <EmptyMessage>
                        📭 Sin asignaciones de equipo
                    </EmptyMessage>
                ) : (
                    team.map((member, index) => {
                        const load = member.load || 0
                        const color = getLoadColor(load)

                        return (
                            <MemberItem key={member.userId || index}>
                                <MemberHeader>
                                    <MemberName>👤 {member.displayName}</MemberName>
                                </MemberHeader>

                                {isAdmin && (
                                    <>
                                        <MemberLoad>
                                            <LoadLabel>Dedicación:</LoadLabel>
                                            <LoadValue color={color}>{load}%</LoadValue>
                                        </MemberLoad>

                                        <ProgressBar>
                                            <ProgressFill width={load} color={color} />
                                        </ProgressBar>
                                    </>
                                )}
                            </MemberItem>
                        )
                    })
                )}
            </ToastMembers>

            <ViewDetailsButton onClick={handleViewDetails}>
                Ver detalles de la oportunidad →
            </ViewDetailsButton>
        </ToastContainer>
    )
}
